import { FC, useEffect } from 'react';
import {
  Box,
  Container,
  ContainerProps,
  Flex,
  Heading,
  HStack,
  Link,
  Skeleton,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import { Helmet } from 'react-helmet-async';
import { BiFolder } from 'react-icons/bi';
import {
  generatePath,
  Link as ReactRouterLink,
  useNavigate,
} from 'react-router-dom';

import * as Routes from '../../constants/routes';
import {
  DataRoomObjectFragment,
  useGetDataRoomsQuery,
} from '../../graphql/generated';
import { DebounceLoadingState } from '../../ui/debounce-loading/debounce-loading';
import { EmptyState } from '../../ui/errors/empty-state';
import { ErrorState } from '../../ui/errors/error-state';
import { FolderIcon } from '../../ui/icons/folder';
import { getAvailableDataRooms } from '../../utils/treaty-utils/get-available-data-rooms';
import { navigateToDirectory } from '../file-browser-page/file-browser-page';

export const navigateToDataRoomsPage = (): string =>
  generatePath(Routes.DATA_ROOMS);

const PageLayout: FC<ContainerProps> = ({ children, ...rest }) => {
  return (
    <>
      <Helmet>
        <title>Data Rooms</title>
      </Helmet>
      <Container pt={8} maxW="100%" data-testid="data-rooms-page" {...rest}>
        {children}
      </Container>
    </>
  );
};

export const DataRoomsPage: FC = () => {
  const navigate = useNavigate();
  const { error, data } = useGetDataRoomsQuery();

  useEffect(() => {
    if (!data?.dataRoomCategories) return;

    const availableDataRooms = getAvailableDataRooms(data);

    // Check if the user has access to just a single data room
    const hasSingleDataRoomAccess = availableDataRooms.length === 1;

    if (hasSingleDataRoomAccess) {
      const [dataRoom] = availableDataRooms;
      // If so, redirect the user directly into it
      navigate(navigateToDirectory({ cedantId: dataRoom.cedant }), {
        replace: true,
      });
    }
  }, [data, navigate]);

  if (error) {
    return (
      <PageLayout display="flex" flex="1">
        <ErrorState
          title="Unable to load data rooms"
          subtitle="Please reach out to the technical support team for further assistance."
        />
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout px="8" py="16">
        <DebounceLoadingState>
          <Stack spacing="5">
            <Skeleton h="10" w="60" />
            <Skeleton h="6" w="80" />
          </Stack>
          <HStack mt="16" spacing="8">
            <Skeleton h="20" w="24" />
            <Skeleton h="20" w="24" />
            <Skeleton h="20" w="24" />
          </HStack>
        </DebounceLoadingState>
      </PageLayout>
    );
  }

  if (isEmpty(data.dataRoomCategories)) {
    return (
      <PageLayout display="flex" flex="1">
        <EmptyState
          title="No data rooms found for this account"
          subtitle="If you think this is an error, please get in touch with an administrator."
          icon={BiFolder}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout px={8} data-testid="dataroom-categories">
      <Heading as="h1" fontSize="1.8rem">
        Data Rooms
      </Heading>

      <Stack px={4} pt={10} pb={12} spacing={16}>
        {data.dataRoomCategories.map((dataRoomCategory) => (
          <Box key={dataRoomCategory.id} data-testid="dataroom-category">
            <Heading as="h2" fontSize="2xl" fontWeight="semibold">
              {dataRoomCategory.name}
            </Heading>
            <Wrap mt="8" spacing="16" direction="row">
              {dataRoomCategory.dataRooms.objects.map((dataRoom) => {
                return <DataRoomLink key={dataRoom.id} dataRoom={dataRoom} />;
              })}
            </Wrap>
          </Box>
        ))}
      </Stack>
    </PageLayout>
  );
};

const DataRoomLink: FC<{ dataRoom: DataRoomObjectFragment }> = ({
  dataRoom,
}) => {
  const linkPath = navigateToDirectory({
    cedantId: dataRoom.cedant.toString(),
  });

  return (
    <WrapItem width="28" justifyContent="center">
      <Link
        to={linkPath}
        as={ReactRouterLink}
        aria-label={dataRoom.name}
        _hover={{ textDecor: 'none' }}
        data-testid="dataroom"
      >
        <Flex
          gridGap="1"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          _focus={{ outline: 'none' }}
        >
          <FolderIcon width="16" height="16" />
          <Text fontWeight="600" align="center">
            {dataRoom.name}
          </Text>
        </Flex>
      </Link>
    </WrapItem>
  );
};
